import { inject, trackGameState } from "@spawn/core";
import { FreegameService } from "@spawn/freegames";
import { FreeRoundState } from "@spawn/networking";
import { RevealService } from "@spawn/reveal";
import {
	BackgroundTexture,
	SlotMachineIdleState as BaseSlotMachineIdleState,
	ModalCloseEvent,
	ModalType,
	SlotMachineButtonId,
	TrackingEvents,
	UiEvents,
	showBackgroundEvent,
	updateAllSymbolsOnSpotlight,
} from "@spawn/slot-machine";
import { filter, first } from "rxjs";

@trackGameState(TrackingEvents.Idle, { skipOnLeave: true })
export class SlotMachineIdleState extends BaseSlotMachineIdleState {
	@inject private readonly revealService!: RevealService;
	@inject private readonly freegameService!: FreegameService;

	public async onEnter(): Promise<void> {
		if (this.networkService.resuming && this.freegameService.state?.currentRound) {
			this.freegameService.isActive = true;
			this.eventDispatcher.dispatch(updateAllSymbolsOnSpotlight());
			this.eventDispatcher.dispatch(showBackgroundEvent(BackgroundTexture.SideGame));
		}

		// If we are in free rounds, we need to wait for any modal to close before uncovering the mysteries.
		if (this.freeRoundsService.isActive) {
			if (
				(this.freeRoundsService.state === FreeRoundState.Starting || this.freeRoundsService.state === FreeRoundState.Running) &&
				!this.freeRoundsService.hasShownStartModal
			) {
				this.waitForModalCloseThenUncover(ModalType.FreeRoundsStart);
			}

			if (this.freeRoundsService.state === FreeRoundState.Finished) {
				this.waitForModalCloseThenUncover(ModalType.FreeRoundsEnd);
			}
		} else {
			// Nothing is listening for a spin press yet (super.onEnter() adds that below), so the
			// press is unambiguously a request to skip the uncover.
			await this.uncoverRequiredMysteries(true);
		}

		await super.onEnter();
	}

	private waitForModalCloseThenUncover(modalType: ModalType): void {
		this.subscriptions.add(
			this.eventDispatcher
				.event$<ModalCloseEvent>(UiEvents.ModalClose)
				.pipe(filter((event) => event.payload.modalType === modalType))
				// Not skippable: super.onEnter() has already run here, so a press would skip the
				// uncover and start a spin at the same time.
				.subscribe(() => this.uncoverRequiredMysteries(false))
		);
	}

	private async uncoverRequiredMysteries(allowSkip: boolean): Promise<void> {
		if (!this.networkService.resuming && !this.revealService.mysteriesAreUncovered) {
			const symbols = this.revealService.getMysterySymbols();
			const freegamesHasStarted = (this.freegameService.state?.currentRound ?? 0) > 0;

			// This case happens when restoring after a nudge-spin where no win occurs.
			if (symbols.length > 0 && !freegamesHasStarted) {
				this.setButtonStates();
				this.buttonStateController.updateState(
					[
						SlotMachineButtonId.Bet,
						SlotMachineButtonId.MaxBet,
						SlotMachineButtonId.PayTable,
						SlotMachineButtonId.QuickMenu,
						SlotMachineButtonId.Spin,
						SlotMachineButtonId.AutoSpin,
					],
					{
						isEnabled: false,
					}
				);

				if (allowSkip) {
					// Skipping an animation is a quick stop, so honour the casino setting for it.
					this.buttonStateController.updateState([SlotMachineButtonId.Spin], {
						isEnabled: this.networkService.isQuickStopAllowed,
					});
					this.subscribeToUncoverSkip();
				}

				await this.revealService.uncover(symbols);
				this.setButtonStates();
			}
		}
	}

	private subscribeToUncoverSkip(): void {
		this.subscriptions.add(
			this.eventDispatcher
				.event$(UiEvents.Spin)
				.pipe(first())
				.subscribe(() => {
					if (this.networkService.isQuickStopAllowed) {
						this.revealService.skip();
					}
				})
		);
	}
}
